'use strict'

/**
 * 最重量計算 statusByCode（列コード→マッピング状態。大きな mappingList の全走査）を Web Worker で算出するフック。
 * 大量マッピングでもメインスレッド（スクロール・操作）をブロックしないよう、計算をバックグラウンドに逃がす。
 *
 * 重要（コードレビュー反映）: 入力（statusInput）は (allColumnCodes / mappingList / mappingListMonthly / periodKind) のみ。
 * モード・検索・行フィルタは含めない → 検索やモード切替で statusByCode が無駄に再計算されず、参照も安定する（メモ化された行が再レンダーをスキップできる）。
 *
 * フォールバック設計:
 *
 * - Worker 非対応環境（jsdom/テスト・SSR・極端に古いブラウザ）や Worker 生成失敗・実行時エラー時は、 メインスレッドで computeStatusByCode を同期実行する（挙動はリファクタ前と同一）。
 * - これによりテストは Worker を一切起動せず、従来どおり同期的に statusByCode を得られる。
 *
 * 入力（statusInput）は呼び出し側で useMemo により安定参照化しておくこと。
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { computeStatusByCode, EMPTY_STATUS_BY_CODE } from './matrixDerivedCompute'
import { logMatrixPerf } from './matrixPerf'

/**
 * 現在の実行環境が Web Worker を使えるか判定する。
 *
 * @returns {boolean}
 */
function supportsWorker() {
  return typeof window !== 'undefined' && typeof window.Worker === 'function'
}

/**
 * @param {import('./matrixDerivedCompute').StatusByCodeInput} statusInput - 安定参照化済みの計算入力
 * @returns {{ statusByCode: { [code: string]: 'set' | 'unset' | 'remove' }; deriving: boolean }}
 */
export function useStatusByCode(statusInput) {
  // Worker を使うか。生成・実行に失敗したら sync へ恒久フォールバックする（state なので再レンダーで切替わる）。
  const [workerMode, setWorkerMode] = useState(() => supportsWorker())
  const [asyncStatus, setAsyncStatus] = useState(EMPTY_STATUS_BY_CODE)
  const [deriving, setDeriving] = useState(false)

  const workerRef = useRef(null)
  const reqRef = useRef(0)

  // 同期パス（Worker 非対応/失敗時）: 従来どおり useMemo で同期計算する。
  const syncStatus = useMemo(
    () => (workerMode ? EMPTY_STATUS_BY_CODE : computeStatusByCode(statusInput)),
    [workerMode, statusInput]
  )

  // Worker の生成とメッセージ購読。workerMode が変わったときだけ再構成する。
  useEffect(() => {
    if (!workerMode) return undefined

    if (!workerRef.current) {
      try {
        workerRef.current = new Worker(new URL('./matrixDerived.worker.js', import.meta.url), { type: 'module' })
      } catch (err) {
        // 生成不可 → 同期フォールバックへ
        setWorkerMode(false)
        return undefined
      }
    }
    const worker = workerRef.current

    const handleMessage = (event) => {
      const data = event.data || {}
      if (data.reqId !== reqRef.current) return // 古い応答は破棄
      if (data.error) {
        // 計算エラー → 同期フォールバックへ（次回以降はメインスレッドで計算）
        setWorkerMode(false)
        setDeriving(false)
        return
      }
      logMatrixPerf('statusByCode(worker message)', 0)
      setAsyncStatus(data.statusByCode)
      setDeriving(false)
    }
    const handleError = () => {
      setWorkerMode(false)
      setDeriving(false)
    }

    worker.addEventListener('message', handleMessage)
    worker.addEventListener('error', handleError)
    return () => {
      worker.removeEventListener('message', handleMessage)
      worker.removeEventListener('error', handleError)
    }
  }, [workerMode])

  // 入力が変わるたびに Worker へ送信。reqId を更新して最新応答だけ採用する。
  // statusInput は mapping/period のみに依存するので、検索・モード・行フィルタでは再送信されない。
  useEffect(() => {
    if (!workerMode || !workerRef.current) return
    const reqId = reqRef.current + 1
    reqRef.current = reqId
    setDeriving(true)
    workerRef.current.postMessage({ reqId, input: statusInput })
  }, [workerMode, statusInput])

  // アンマウント時に Worker を破棄する。
  useEffect(
    () => () => {
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
    },
    []
  )

  if (workerMode) {
    return { statusByCode: asyncStatus, deriving }
  }
  return { statusByCode: syncStatus, deriving: false }
}
